import { fade, makeStyles } from '@material-ui/core/styles'

/**
 * The detail screen carried inline styles only. The panel's established shape
 * is a `styles/<screen>.styles.ts` per screen — SupportTickets and
 * SupportSlaPolicies both have one — so the action bar's layout lives here
 * rather than adding another inline style object to the JSX.
 */
const useStyles = makeStyles((theme) => ({
  // Conversation left, reference right — the layout every comparable support
  // tool converges on, because the conversation is the work and the metadata is
  // reference material consulted occasionally.
  //
  // CSS grid rather than MUI's <Grid>: this panel uses `xs`/`sm` Grid props for
  // FORM ROWS, and `md`/`lg` props appear ZERO times in src/. Its actual
  // responsive mechanism is JSS media queries — `theme.breakpoints.down('sm')`
  // alone appears 859 times. A <Grid item md={8}> here would be the novel
  // construct, not the conventional one.
  //
  // MOBILE-FIRST — one column is the base, two is the enhancement — and `up`,
  // never `down`. This is not style preference. In MUI **v4**, `down(key)` does
  // NOT mean "below key": createBreakpoints.js:41 reads
  // `values[keys[indexOf(key) + 1]]`, so `down('md')` emits
  // `max-width: 1279.95px` — md AND EVERYTHING BELOW. v5 changed that to mean
  // what it reads like. An earlier revision of this file used `down('md')`
  // believing it collapsed at 960; the emitted CSS was 1279.95 and both this
  // comment and the MR description described a layout that did not exist.
  // `up()` means min-width in both versions, so the trap cannot recur here.
  //
  // 1280 (v4 `lg`) rather than 960: at 960 the narrow track is ~300px, and its
  // binding content is unbreakable LTR runs — `type:code` entity refs and
  // `policyId v<n>` — plus a two-clause SLA line. Below roughly a 320px track
  // those wrap into a ragged 4-6 line ribbon per field. minmax(0, …) stops the
  // horizontal scroll but converts the overflow into that wrapping, so it
  // bounds the damage rather than removing it.
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)'
    }
  },
  // No RTL handling is needed and none is written: CSS grid places tracks along
  // the INLINE axis, which follows `direction`. In Farsi the 2fr track lands on
  // the right — the reading-start side — so the conversation stays first in
  // both directions for the same reason it is first in the DOM.
  //
  // The conversation is FIRST in the DOM, so it is first on a narrow screen and
  // first to a screen reader. Achieving the same visual result with `order` on
  // a reference-first DOM would desync reading order from visual order, which
  // is the classic grid accessibility defect.
  //
  // minWidth: 0 on the items as well as minmax(0, …) on the tracks. The two
  // target the same automatic-minimum-size rule and the track-level fix is
  // normally sufficient; this is deliberate belt-and-braces on a column whose
  // content is specifically the unbreakable strings that trigger it, kept
  // because removing it can only be validated by rendering, not by a test.
  main: { minWidth: 0 },
  aside: { minWidth: 0 },
  // `gap` rather than per-button margins: the bar's children are conditional
  // (transitions vary by state, escalate is role-dependent), so a trailing
  // margin on each would leave a stray gap whenever the last one is absent.
  //
  // The margin fallback is not belt-and-braces — `gap` has two silent failure
  // modes here, and both produce ZERO spacing rather than a visible break:
  // jss-plugin-default-unit only learned `gap` in jss 10.5.0 (before that it
  // emits `gap: 8`, unitless, which the browser drops), and flex-container
  // `gap` is Safari 14.1+. Buttons rendered flush against each other are a
  // mis-click risk, so the fallback applies unless @supports confirms gap.
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    '& > *:not(:last-child)': { marginInlineEnd: theme.spacing(1) },
    '@supports (gap: 8px)': {
      '& > *:not(:last-child)': { marginInlineEnd: 0 }
    }
  },
  // The transitions and their label form one group. Same spacing rules as the
  // bar, for the same reasons.
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    '& > *:not(:last-child)': { marginInlineEnd: theme.spacing(1) },
    '@supports (gap: 8px)': {
      '& > *:not(:last-child)': { marginInlineEnd: 0 }
    }
  },
  // marginInlineEnd, not marginRight: this panel renders in Farsi.
  //
  // uppercase and letterSpacing are scoped to LTR deliberately. Persian has no
  // case, so `uppercase` is inert there and the label silently loses the weight
  // the rule was buying. letterSpacing is worse than inert: Arabic script is
  // cursive and joins, so forcing inter-character space stretches or breaks the
  // joins — wrong to a native reader in a way that is not visible to a reviewer
  // working in English.
  actionLabel: {
    marginInlineEnd: theme.spacing(0.5),
    fontWeight: 700,
    ...(theme.direction === 'ltr' && {
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em'
    })
  },
  // One card surface, replacing eight copies of `style={{ padding: 16,
  // marginBottom: 16 }}` spread across this screen's seven components. The
  // literals happened to land on the 8px grid, but they bypassed it — and the
  // duplication is why the rail could not be demoted without editing eight
  // places. `panelCardQuiet` is that demotion: reference material reads as
  // reference, and the conversation keeps the only raised surface on screen.
  panelCard: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  panelCardQuiet: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  // The composer takes the tint of the audience it will post to.
  //
  // warning for internal, matching `needsAttentionRow` in the queue's styles so
  // "amber means look here" stays one idea across the feature. It is the WEAKEST
  // of the four scope signals by design — the badge text, the radio label and
  // the submit button all name the audience in words, so 1.4.1 is satisfied
  // without any of this and a reader who cannot see the tint loses nothing.
  //
  // borderInlineStart, not borderLeft: in Farsi the bar belongs on the right.
  composerInternal: {
    borderInlineStart: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: fade(theme.palette.warning.main, 0.08),
    paddingInlineStart: theme.spacing(1.5),
    paddingBlock: theme.spacing(1)
  },
  composerPublic: {
    borderInlineStart: `4px solid ${theme.palette.divider}`,
    paddingInlineStart: theme.spacing(1.5),
    paddingBlock: theme.spacing(1)
  },
  scopeBadgeInternal: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 700,
    color: theme.palette.warning.dark,
    marginBottom: theme.spacing(0.5)
  },
  scopeBadgePublic: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  scopeBadgeIcon: { marginInlineEnd: theme.spacing(0.5) },
  // The look of a disabled button without the native attribute, which removes
  // the node from the tab order and drops focus to <body>. See the button.
  buttonDisabled: {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: 'none',
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none'
    }
  },
  // Internal comments in the thread carry the same amber as the composer that
  // produced them, so a note reads the same way it was written.
  commentInternal: {
    borderInlineStart: `3px solid ${theme.palette.warning.main}`,
    backgroundColor: fade(theme.palette.warning.main, 0.06),
    paddingInlineStart: theme.spacing(1),
    marginBlock: theme.spacing(1)
  }
}))

export default useStyles
